import { automaticSeeds, selectRecommendations, validVariant, analyticsPayload, allowedSeedCollections } from '#cart-recommendations-state';

function readArray(value) {
  try { const parsed = JSON.parse(value || '[]'); return Array.isArray(parsed) ? parsed.map(String) : []; }
  catch { return []; }
}

function readCandidates(root, sourceId) {
  return [...root.querySelectorAll('template[data-recommendation-card]')].map((template) => ({
    id: template.dataset.productId, type: template.dataset.productType,
    source: template.dataset.source, sourceId: sourceId || template.dataset.sourceId,
    collections: readArray(template.dataset.collections), quickAdd: template.dataset.quickAdd === 'true',
    template,
  }));
}

export class CartDrawerUpsell extends HTMLElement {
  constructor() {
    super();
    this.generation = 0;
    this.seen = new Set();
    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onConsentChange = () => { if (this.opened) this.render(); };
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    document.addEventListener('visitorConsentCollected', this.onConsentChange);
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      try {
        const html = event.cartData?.sections?.['cart-drawer'];
        if (html && this.dataset.placement !== 'pdp') this.replaceState(html);
        else this.refreshState();
      } catch {
        this.classList.add('hidden');
      }
    });
    this.observer = new IntersectionObserver((entries) => {
      if (!this.opened) return;
      for (const entry of entries) {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue;
        const candidate = this.selected?.find((item) => item.id === entry.target.dataset.productId);
        if (!candidate) continue;
        const key = [candidate.id, candidate.source, candidate.sourceId].join(':');
        if (!this.seen.has(key) && this.track('view', candidate)) this.seen.add(key);
      }
    }, { root: this.closest('.cart-drawer__scrollable'), threshold: 0.5 });
    this.readState();
    if (this.opened) this.onDrawerOpen();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    this.observer?.disconnect();
    this.cancel();
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    document.removeEventListener('visitorConsentCollected', this.onConsentChange);
  }

  get opened() {
    return this.closest('cart-drawer')?.classList.contains('active') === true;
  }

  cancel() {
    this.generation += 1;
    this.automaticController?.abort();
    this.refreshController?.abort();
    this.automaticRequested = false;
  }

  replaceState(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source = this.dataset.placement === 'pdp'
      ? parsed.getElementById(this.id)?.querySelector('template[data-recommendation-state]')
      : parsed.querySelector('template[data-recommendation-state]');
    const target = this.querySelector('template[data-recommendation-state]');
    if (!source || !target) { this.stale = true; this.render(); return; }
    this.lastStateAt = Date.now();
    if (source.innerHTML === target.innerHTML && !this.stale) return;
    target.replaceWith(source);
    this.readState();
  }

  readState() {
    this.cancel();
    this.stale = false;
    const root = this.querySelector('template[data-recommendation-state]')?.content;
    this.config = root?.querySelector('[data-recommendation-config]')?.dataset || {};
    this.state = {
      cartIds: (this.config.cartIds || '').split(',').filter(Boolean),
      seeds: [...(root?.querySelectorAll('[data-recommendation-seed]') || [])].map((seed) => ({
        id: seed.dataset.productId, type: seed.dataset.productType,
        value: Number(seed.dataset.value), allowed: allowedSeedCollections(seed.dataset.productType,
          readArray(seed.dataset.allowedCollections), readArray(seed.dataset.allowedHandles)),
      })),
      candidates: root ? readCandidates(root) : [],
    };
    this.automaticCandidates = [];
    this.lastStateAt = Date.now();
    this.render();
    this.loadAutomatic();
  }

  onDrawerOpen() {
    this.render();
    if (Date.now() - this.lastStateAt > 1000) this.refreshState();
    else this.loadAutomatic();
  }

  onDrawerClose() {
    this.cancel();
    this.seen.clear();
  }

  async refreshState() {
    if (!this.config.cartUrl) return;
    this.cancel();
    this.stale = true;
    this.render();
    const generation = this.generation;
    const controller = new AbortController();
    this.refreshController = controller;
    const timeout = setTimeout(() => controller.abort(), 3500);
    try {
      const url = new URL(this.dataset.productUrl || this.config.cartUrl, window.location.origin);
      const sectionId = this.dataset.sectionId || 'cart-drawer';
      url.searchParams.set('sections', sectionId);
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) return;
      const sections = await response.json();
      if (generation !== this.generation || !this.isConnected) return;
      if (sections[sectionId]) this.replaceState(sections[sectionId]);
      this.loadAutomatic();
    } catch {
      // Cart and checkout remain usable when optional recommendations cannot refresh.
    } finally {
      clearTimeout(timeout);
    }
  }

  async loadAutomatic() {
    if (this.stale || !this.opened || this.config.enabled !== 'true' || this.config.automatic !== 'true' ||
        this.selected.length >= 4 ||
        this.automaticRequested || !this.config.url) return;
    const seeds = automaticSeeds(this.state.seeds);
    if (!seeds.length) return;
    this.automaticRequested = true;
    const generation = this.generation;
    const controller = new AbortController();
    this.automaticController = controller;
    const timeout = setTimeout(() => controller.abort(), 3500);
    try {
      const results = await Promise.allSettled(seeds.map(async (seed) => {
        const url = new URL(this.config.url, window.location.origin);
        url.searchParams.set('product_id', seed.id);
        url.searchParams.set('limit', '10');
        url.searchParams.set('intent', 'related');
        url.searchParams.set('section_id', 'cart-recommendation-candidates');
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) return [];
        const html = await response.text();
        return readCandidates(new DOMParser().parseFromString(html, 'text/html'), seed.id);
      }));
      if (generation !== this.generation || !this.opened || !this.isConnected) return;
      this.automaticCandidates = results.flatMap((result) => result.status === 'fulfilled' ? result.value : []);
      this.render();
    } catch {
      // Malformed optional results must not interrupt cart actions.
    } finally {
      clearTimeout(timeout);
    }
  }

  render() {
    const track = this.querySelector('.swiper-wrapper');
    if (!track || !this.state) return;
    const selected = !this.stale && this.config.enabled === 'true'
      ? selectRecommendations({ ...this.state, candidates: [...this.state.candidates, ...this.automaticCandidates] }) : [];
    const scroll = this.closest('.cart-drawer__scrollable');
    const scrollTop = scroll?.scrollTop;
    const active = document.activeElement;
    const activeSlide = this.contains(active) ? active.closest('[data-product-id]') : null;
    const focusSelector = active?.matches('select') ? 'select' : active?.matches('button') ? 'button'
      : active?.matches('.cart-drawer__upsell-title') ? '.cart-drawer__upsell-title'
      : active?.matches('.cart-drawer__upsell-details') ? '.cart-drawer__upsell-details' : '[data-recommendation-link]';
    const swiper = this.querySelector('swiper-component')?.swiper;
    const activeProduct = track.children[swiper?.activeIndex || 0]?.dataset.productId;
    const previous = new Map([...track.children].map((slide) => [slide.dataset.productId, slide]));
    this.observer?.disconnect();
    const nodes = selected.map((candidate) => {
      const old = previous.get(candidate.id);
      const html = candidate.template.innerHTML;
      if (old?.recommendationHTML === html) return old;
      const node = candidate.template.content.firstElementChild.cloneNode(true);
      if (this.dataset.placement === 'pdp') {
        for (const element of node.querySelectorAll('[id], [for], [aria-describedby]')) {
          for (const attribute of ['id', 'for', 'aria-describedby']) {
            const value = element.getAttribute(attribute);
            if (value) element.setAttribute(attribute, `${this.id}-${value}`);
          }
        }
      }
      node.recommendationHTML = html;
      const oldSelection = old?.querySelector('select')?.value;
      const select = node.querySelector('select');
      if (oldSelection && select) {
        const option = [...select.options].find((entry) => entry.value === oldSelection);
        if (validVariant(option)) {
          select.value = oldSelection;
          this.updateVariant(select);
        }
      }
      return node;
    });
    this.selected = selected;
    track.replaceChildren(...nodes);
    this.classList.toggle('hidden', nodes.length === 0);
    swiper?.update();
    const newActive = nodes.findIndex((node) => node.dataset.productId === activeProduct);
    if (newActive >= 0) swiper?.slideTo(newActive, 0);
    else if (nodes.length) swiper?.slideTo(Math.min(swiper?.activeIndex || 0, nodes.length - 1), 0);
    if (activeSlide && (document.activeElement !== active || !active.isConnected || !nodes.length)) {
      const replacement = nodes.find((node) => node.dataset.productId === activeSlide.dataset.productId);
      (active.isConnected ? active : replacement?.querySelector(focusSelector) || this.closest('cart-drawer')?.querySelector('.drawer__close'))?.focus({ preventScroll: true });
    }
    if (scroll && scrollTop !== undefined) scroll.scrollTop = scrollTop;
    for (const node of nodes) this.observer?.observe(node);
  }

  onClick(event) {
    const slide = event.target.closest('.cart-drawer__upsell-slide');
    const candidate = this.selected?.find((item) => item.id === slide?.dataset.productId);
    if (!candidate) return;
    const button = event.target.closest('button.cart-drawer__upsell-add');
    if (button) {
      this.addToCart(button, candidate);
    } else if (event.target.closest('[data-recommendation-link]')) {
      this.track('click', candidate);
    }
  }

  onChange(event) {
    if (event.target.matches('.cart-drawer__upsell-variant-select')) this.updateVariant(event.target);
  }

  updateVariant(select) {
    const option = select.selectedOptions[0];
    const card = select.closest('.cart-drawer__upsell-card');
    const button = card.querySelector('button.cart-drawer__upsell-add');
    const valid = validVariant(option);
    if (button) {
      button.dataset.variantId = valid ? option.value : '';
    }
    if (!valid) return;
    const prompt = card.querySelector('[data-upsell-variant-prompt]');
    if (prompt) prompt.hidden = true;
    select.removeAttribute('aria-invalid');
    select.removeAttribute('aria-describedby');
    for (const link of card.querySelectorAll('[data-recommendation-link]')) {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('variant', option.value);
      link.href = url.href;
    }
    card.querySelector('[data-upsell-from]').hidden = true;
    const price = card.querySelector('[data-upsell-price]');
    const compare = card.querySelector('[data-upsell-compare-price]');
    price.textContent = option.dataset.price;
    price.classList.toggle('cart-item__final-price--sale', Boolean(option.dataset.comparePrice));
    compare.textContent = option.dataset.comparePrice || '';
    // The "UVP" label and the struck price show and hide together.
    const compareGroup = card.querySelector('[data-upsell-compare-group]') || compare;
    compareGroup.classList.toggle('hidden', !option.dataset.comparePrice);
    // Saisoneröffnung: a campaign variant's struck price is the 30-day price, no "UVP".
    const isUvp = option.dataset.compareUvp !== 'false';
    card.querySelector('[data-upsell-uvp-label]')?.toggleAttribute('hidden', !isUvp);
    card.querySelector('[data-upsell-regular-label]')?.toggleAttribute('hidden', isUvp);
    // Grundpreis follows the chosen variant (empty when it has none).
    const unitPrice = card.querySelector('[data-upsell-unit-price]');
    if (unitPrice) {
      const value = unitPrice.querySelector('[data-upsell-unit-price-value]');
      if (value) value.textContent = option.dataset.unitPrice || '';
      unitPrice.classList.toggle('hidden', !option.dataset.unitPrice);
    }
  }

  async addToCart(button, candidate) {
    if (button.disabled || this.adding || !candidate.quickAdd) return;
    const card = button.closest('.cart-drawer__upsell-card');
    const select = card.querySelector('select');
    if (select && !validVariant(select.selectedOptions[0])) {
      const prompt = card.querySelector('[data-upsell-variant-prompt]');
      if (prompt) {
        prompt.hidden = false;
        select.setAttribute('aria-describedby', prompt.id);
      }
      select.setAttribute('aria-invalid', 'true');
      select.focus({ preventScroll: true });
      try { select.showPicker?.(); } catch {}
      return;
    }
    if (!button.dataset.variantId) return;
    const focusWasInCard = card.contains(document.activeElement);
    const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
    const variantId = button.dataset.variantId;
    const errors = document.getElementById('CartDrawer-CartErrors');
    this.adding = true;
    button.disabled = true;
    button.classList.add('loading');
    if (select) select.disabled = true;
    this.cancel();
    if (errors) errors.textContent = '';
    const linesUpdate = this.createLinesUpdate(variantId);
    try {
      const response = await fetch(routes.cart_add_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: drawer.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.errors) throw new Error(result.description || window.cartStrings.error);
      this.resolveLinesUpdate(linesUpdate);
      this.track('add', candidate, variantId);
      drawer.renderContents(result);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: result });
    } catch (error) {
      linesUpdate?.reject(error);
      if (errors) errors.textContent = error.message || window.cartStrings.error;
    } finally {
      this.adding = false;
      button.disabled = false;
      button.classList.remove('loading');
      if (select) select.disabled = false;
      // Disabling the activated button can move browser focus to the body
      // before the refreshed cards can capture it. Do not steal focus if the
      // shopper deliberately moved to another control while the request ran.
      if (focusWasInCard && this.opened && document.activeElement === document.body) {
        (card.isConnected ? select || button : drawer.querySelector('.drawer__close'))?.focus({ preventScroll: true });
      }
    }
  }

  createLinesUpdate(variantId) {
    try {
      const EventClass = window.StandardEvents?.CartLinesUpdateEvent;
      if (!EventClass) return null;
      const deferred = EventClass.createPromise();
      this.dispatchEvent(new EventClass({
        action: 'add', context: 'cart',
        lines: [{ merchandiseId: Number(variantId), quantity: 1 }], promise: deferred.promise,
      }));
      return deferred;
    } catch { return null; }
  }

  resolveLinesUpdate(deferred) {
    if (!deferred) return;
    // Match the theme's existing standard cart event pipeline. /cart/add only
    // returns added lines, so obtain the confirmed aggregates separately.
    const request = typeof CartItems !== 'undefined' ? CartItems.fetchCartData()
      : fetch(`${this.config.cartUrl}.json`).then((response) => response.json());
    request.then((cart) => {
      if (!cart?.currency) throw new Error('Missing cart currency');
      deferred.resolve({ cart: window.StandardEvents.CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
    }).catch((error) => deferred.reject(error));
  }

  track(action, candidate, variantId) {
    try {
      const shopify = window.Shopify;
      if (!shopify?.customerPrivacy?.analyticsProcessingAllowed?.() || !shopify.analytics?.publish) return false;
      const result = shopify.analytics.publish('cart_recommendation', { ...analyticsPayload(action, candidate, variantId, this.config.currency), placement: this.dataset.placement === 'pdp' ? 'pdp' : 'cart_drawer' });
      result?.catch?.(() => {});
      return true;
    } catch { return false; }
  }
}

if (!customElements.get('cart-drawer-upsell')) customElements.define('cart-drawer-upsell', CartDrawerUpsell);

export class ProductAccessories extends CartDrawerUpsell {
  get opened() { return true; }
}
if (!customElements.get('product-accessories')) customElements.define('product-accessories', ProductAccessories);
